import { useState, useEffect, useCallback } from 'react';
import { Logger } from 'src/utils/logger';
import { VideoAnalysisTriggerRequest } from 'src/services/video-analysis/video-analysis.service';

interface ContentState {
  blog?: string;
  ig_captions?: string;
  email_text?: string;
}

interface ContentStore {
  content: Record<string, ContentState>; // keyed by mediaId
  loading: Record<string, boolean>; // loading states per mediaId
  errors: Record<string, string | null>; // errors per mediaId
}

export const useContentState = () => {
  const [store, setStore] = useState<ContentStore>({
    content: {},
    loading: {},
    errors: {}
  });

  const logger = Logger.createScopedLogger('useContentState');

  // Listen for content changes from other components
  useEffect(() => {
    const handleContentChange = (event: CustomEvent) => {
      const { mediaId, contentType, content } = event.detail;
      setStore(prev => ({
        ...prev,
        content: {
          ...prev.content,
          [mediaId]: {
            ...prev.content[mediaId],
            [contentType]: content
          }
        }
      }));
    };

    window.addEventListener('contentStateChanged', handleContentChange as EventListener);
    return () => {
      window.removeEventListener('contentStateChanged', handleContentChange as EventListener);
    };
  }, []);

  // Get content for specific media and type
  const getContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']): string | undefined => {
    return store.content[mediaId]?.[contentType];
  }, [store.content]);

  // Check if content exists for specific media and type
  const hasContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']): boolean => {
    const content = store.content[mediaId]?.[contentType];
    return !!content && content.trim().length > 0;
  }, [store.content]);

  // Set content for specific media and type
  const setContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type'], content: string) => {
    setStore(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [mediaId]: {
          ...prev.content[mediaId],
          [contentType]: content
        }
      },
      errors: {
        ...prev.errors,
        [mediaId]: null // Clear any previous errors
      }
    }));
    
    // Trigger a custom event to notify other components
    window.dispatchEvent(new CustomEvent('contentStateChanged', { 
      detail: { mediaId, contentType, content } 
    }));
    
    logger.info(`Content set for ${mediaId}:${contentType}`, { contentLength: content.length });
  }, [logger]);

  // Clear content for specific media and type
  const clearContent = useCallback((mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type']) => {
    setStore(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [mediaId]: {
          ...prev.content[mediaId],
          [contentType]: undefined
        }
      }
    }));
    
    logger.info(`Content cleared for ${mediaId}:${contentType}`);
  }, [logger]);

  // Set loading state for specific media
  const setLoading = useCallback((mediaId: string, loading: boolean) => {
    setStore(prev => ({
      ...prev,
      loading: {
        ...prev.loading,
        [mediaId]: loading
      }
    }));
  }, []);

  // Set error for specific media
  const setError = useCallback((mediaId: string, error: string | null) => {
    setStore(prev => ({
      ...prev,
      errors: {
        ...prev.errors,
        [mediaId]: error
      }
    }));
    
    if (error) {
      logger.error(`Content error for ${mediaId}`, error);
    }
  }, [logger]);

  // Get loading state for specific media
  const isLoading = useCallback((mediaId: string): boolean => {
    return store.loading[mediaId] || false;
  }, [store.loading]);

  // Get error for specific media
  const getError = useCallback((mediaId: string): string | null => {
    return store.errors[mediaId] || null;
  }, [store.errors]);

  // Load existing content for media (placeholder for API integration)
  const loadContent = useCallback(async (mediaId: string) => {
    if (!mediaId) return;

    setLoading(mediaId, true);
    setError(mediaId, null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // const existingContent = await ContentAPI.getContent(mediaId);
      
      // For now, check localStorage as a temporary solution
      const storageKey = `content_${mediaId}`;
      const storedContent = localStorage.getItem(storageKey);
      
      if (storedContent) {
        try {
          const parsedContent = JSON.parse(storedContent) as ContentState;
          setStore(prev => ({
            ...prev,
            content: {
              ...prev.content,
              [mediaId]: parsedContent
            }
          }));
          logger.info(`Loaded content from localStorage for ${mediaId}`);
        } catch (parseError) {
          logger.warn(`Failed to parse stored content for ${mediaId}`, parseError);
        }
      }
    } catch (error) {
      logger.error(`Failed to load content for ${mediaId}`, error);
      setError(mediaId, 'Failed to load existing content');
    } finally {
      setLoading(mediaId, false);
    }
  }, [logger, setLoading, setError]);

  // Save content for media (placeholder for API integration)
  const saveContent = useCallback(async (mediaId: string, contentType: VideoAnalysisTriggerRequest['request_type'], content: string) => {
    if (!mediaId || !content.trim()) return false;

    setLoading(mediaId, true);
    setError(mediaId, null);

    try {
      // TODO: Replace with actual API call when backend is ready
      // await ContentAPI.saveContent(mediaId, contentType, content);
      
      // For now, save to localStorage as a temporary solution
      const storageKey = `content_${mediaId}`;
      const existingContent = store.content[mediaId] || {};
      const updatedContent = {
        ...existingContent,
        [contentType]: content
      };
      
      localStorage.setItem(storageKey, JSON.stringify(updatedContent));
      
      // Update local state
      setContent(mediaId, contentType, content);
      
      logger.info(`Saved content to localStorage for ${mediaId}:${contentType}`);
      return true;
    } catch (error) {
      logger.error(`Failed to save content for ${mediaId}:${contentType}`, error);
      setError(mediaId, 'Failed to save content');
      return false;
    } finally {
      setLoading(mediaId, false);
    }
  }, [logger, store.content, setContent, setLoading, setError]);

  // Get all content types that have content for a media
  const getContentTypes = useCallback((mediaId: string): VideoAnalysisTriggerRequest['request_type'][] => {
    const mediaContent = store.content[mediaId];
    if (!mediaContent) return [];

    return Object.entries(mediaContent)
      .filter(([_, content]) => content && content.trim().length > 0)
      .map(([type, _]) => type as VideoAnalysisTriggerRequest['request_type']);
  }, [store.content]);

  return {
    // Content operations
    getContent,
    hasContent,
    setContent,
    clearContent,
    loadContent,
    saveContent,
    getContentTypes,
    
    // State queries
    isLoading,
    getError,
    
    // Raw store access (for debugging)
    store
  };
};
